import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type {
  MemberMetric,
  MonthlyMemberDetail,
} from "types/BusinessDashboard";

import { useMemberDetailQuery } from "queries/Business/Dashboard/useDashboardQueries";
import { MemberMonthlyParams } from "api/businessDashboard";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import { MEMBERS_PASS_TYPE_MAP } from "constants/businessDashboard";
import { MembersDataTableSkeleton } from "./members-data-table.skeleton";
import { RealTable } from "./real-table";

dayjs.locale("ko");

export const MembersDataTable = () => {
  const { searchParams } = useUpdateUrlParams();

  const endMonthParam =
    searchParams.get("endMonth") || dayjs().format("YYYY-MM");
  const endDate = dayjs(endMonthParam);

  const referenceEndMonth = useMemo(() => endDate.format("YYYY-MM"), [endDate]);
  const referenceStartMonth = useMemo(
    () => endDate.subtract(5, "month").format("YYYY-MM"),
    [endDate]
  );

  const start = dayjs(referenceStartMonth);
  const end = dayjs(referenceEndMonth);

  const periodText = `${start.format("YY년 M월")} - ${end.format("M월")} 내역`;

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const detailApiParams = useMemo(() => {
    const params: MemberMonthlyParams = {
      referenceStartMonth,
      referenceEndMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [partnerInfo, referenceEndMonth, referenceStartMonth]);

  const {
    data: detailData,
    isLoading,
    isError,
  } = useMemberDetailQuery(detailApiParams);

  if (isLoading) return <MembersDataTableSkeleton periodText={periodText} />;
  if (isError || !detailData) return <></>;

  const monthNames = detailData.data.map((m) =>
    dayjs(m.referenceMonth).format("M월")
  );

  const { tableDetailedData } = (() => {
    if (!detailData) return { tableDetailedData: [] };

    const tableResult = Object.values(MEMBERS_PASS_TYPE_MAP).map(
      ({ title, apiKey }) => ({
        category: title,
        data: detailData.data.map((m) => {
          const metric = m[
            apiKey as keyof Omit<MonthlyMemberDetail, "referenceMonth">
          ] as MemberMetric;
          return {
            value: metric?.count ?? 0,
            changeRate: metric?.changeRateComparedToPreviousMonth,
          };
        }),
      })
    );

    return { tableDetailedData: tableResult };
  })();

  return (
    <RealTable
      periodText={periodText}
      monthNames={monthNames}
      tableDetailedData={tableDetailedData}
    />
  );
};
